import React, { useEffect, useMemo, useState } from "react";
import { LoadingIndicator } from "../LoadingIndicator";
import {
    CategoryChartComparisonComponentProps,
    CategoryChartComponentProps,
    CategoryChartData,
    fetchCategoryChartDataWithVisibility
} from "../categoryChart";
import { useCategoryContext, useProviderComparePageContext, useProviderInfoPageContext } from "../Context";
import { CategoryChart as CategoryChartType, CategoryChartVisType } from "../processors";
import { FigureWithSolidDashLine } from "./FigureWithSolidDashLine";
import { Figure } from "./Figure";
import { DisplayTitle } from "./DisplayTitle";
import { FigureWithHorStackBar } from "./FigureWithHorStackBar";
import { ContextType } from "../misc";
import { FigureProviders } from "./FigureProviders";
import { FigureWithSolidDashLineProviders } from "./FigureWithSolidDashLineProviders";
import { FigureWithHorStackBarProviders } from "./FigureWithHorStackBarProviders";
import { HorizontalTwoLineLabelProviders } from "./HorizontalTwoLineLabelProviders";
import { SupplyDemandChart } from "./SupplyDemandChart";
import { SupplyDemandChartProviders } from "./SupplyDemandChartProviders";
import { ReportingTableProviders } from "./ReportingTableProviders";
import { ReportingTableProvider } from "./ReportingTableProvider";
import { HorizontalChartProvider } from "./HorizontalChartProvider";
import { HorizontalChartProviders } from "./HorizontalChartProviders";

import "./CategoryChart.module.scss";

export const CategoryChart: React.FC<CategoryChartType> = ({
    tid,
    visType,
    className,
    visibilityFn,
    queryFn,
    ...props
}) => {
    const [loading, setLoading] = useState(undefined);
    const [data, setData] = useState<CategoryChartData>(undefined);
    const { contextType: compareContext, metadata: providerComparisonPageMetadata } = useProviderComparePageContext();
    const { contextType: infoContext, metadata: providerInfoPageMetadata } = useProviderInfoPageContext();
    const metadata = useMemo(() => {
        return providerInfoPageMetadata ?? providerComparisonPageMetadata;
    }, [providerInfoPageMetadata, providerComparisonPageMetadata]);

    const { category } = useCategoryContext();
    const { provider } = useProviderInfoPageContext();
    const { provider: fromProvider, toProvider } = useProviderComparePageContext();

    const context: ContextType = infoContext ?? compareContext;

    const register: Record<CategoryChartVisType, React.FC<CategoryChartComponentProps>> = {
        figure: Figure,
        displayTitle: DisplayTitle,
        figureWithSolidDashLine: FigureWithSolidDashLine,
        solidDashline: FigureWithSolidDashLine,
        figureWithStackHorBarChart: FigureWithHorStackBar,
        horBarChart: HorizontalChartProvider,
        // figureWithStackHorBarChart: HorizontalChartProvider,
        stackHorBarChart: FigureWithHorStackBar,
        supplyDemandChart: SupplyDemandChart,
        table: ReportingTableProvider,
        horizontalChart: HorizontalChartProvider,
        horizontalChartWithTwoLineLabel: HorizontalTwoLineLabelProviders
    };

    const comparisonRegister: Record<CategoryChartVisType, React.FC<CategoryChartComparisonComponentProps>> = {
        figure: FigureProviders,
        displayTitle: DisplayTitle,
        figureWithSolidDashLine: FigureWithSolidDashLineProviders,
        solidDashline: FigureWithSolidDashLineProviders,
        // figureWithStackHorBarChart: FigureWithHorStackBarProviders,
        figureWithStackHorBarChart: FigureWithHorStackBarProviders,
        horBarChart: HorizontalChartProviders,

        stackHorBarChart: FigureWithHorStackBarProviders,
        supplyDemandChart: SupplyDemandChartProviders,
        table: ReportingTableProviders,
        horizontalChart: HorizontalChartProvider,
        horizontalChartWithTwoLineLabel: HorizontalTwoLineLabelProviders
    };

    useEffect(
        () => {
            setLoading(true);
            fetchCategoryChartDataWithVisibility(
                metadata,
                category,
                [provider, fromProvider, toProvider].filter(d => d != null),
                {
                    tid,
                    visType,
                    visibilityFn,
                    queryFn,

                    adminId: props.adminId,
                    category: props.category,
                    displayTitle: props.displayTitle,
                    figureFooter: props.figureFooter,
                    figureHeader: props.figureHeader,
                    name: props.name,
                    paramFetchFn: props.paramFetchFn,
                    status: props.status,
                    visFooter: props.visFooter,
                    visGroup: props.visGroup,
                    visHeader: props.visHeader,
                    weight: props.weight
                }
            ).then(d => {
                setLoading(false);
                setData(d);
            });
        },
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [category, tid, provider?.tid, fromProvider?.tid, toProvider?.tid, metadata]
    );

    if (!category || loading === undefined) return null;

    const Component =
        context === "info" ? register?.[visType] : context === "comparison" ? comparisonRegister?.[visType] : undefined;

    return (
        <>
            {loading === true && (
                <div className="col-12">
                    <LoadingIndicator />{" "}
                </div>
            )}
            {loading === false && Component != null && data != null && (
                <div className={`${className ?? "col-12"} category-chart ${visType}`}>
                    <Component
                        categoryChart={{
                            tid,
                            visType,
                            className,
                            visibilityFn,
                            queryFn,
                            ...props
                        }}
                        {...data}
                    />
                </div>
            )}
        </>
    );
};
